/**
 * Edificio en corte: cada piso es un cliente y cada habitación un proyecto.
 * Abajo van las áreas comunes (lobby, sala, cafetería y baños); el elevador de
 * la derecha conecta todos los pisos.
 */
import { hashId } from './pixelKit';
import { sceneFor } from './officeScenes';

export { hashId };

export const FLOOR_H = 250;
export const ROOM_W = 340;
export const ELEV_W = 120;
export const BRICK = 26;
export const ROOF = 132;
export const GROUND = 60;
export const STAGE_H = 640;
/** Altura de la línea de pies sobre el borde inferior del piso. */
export const FEET = 9;

const LOOKS = [
    { wallpaper: 'stripes', wall: '#c5ddb0', trim: '#6e8f58', floor: '#d7b07a' },
    { wallpaper: 'tile', wall: '#f3ddb0', trim: '#c49a58', floor: '#e4c48a' },
    { wallpaper: 'herring', wall: '#b9dcc0', trim: '#5f8468', floor: '#c9965a' },
    { wallpaper: 'dots', wall: '#f6c9d8', trim: '#c47a96', floor: '#e7c39a' },
    { wallpaper: 'brick', wall: '#f0d2b0', trim: '#a67c52', floor: '#d8b07a' },
    { wallpaper: 'dots', wall: '#cfd8f2', trim: '#6878b0', floor: '#d9bd92' },
    { wallpaper: 'plank', wall: '#e7d3b4', trim: '#8d6a42', floor: '#c9a36a' },
    { wallpaper: 'stripes', wall: '#d5e6ee', trim: '#5a8aa0', floor: '#d7c09a' },
];

const COMMON = {
    lobby: { id: 'lobby-sala', kind: 'lobby', name: 'Lobby', look: { wallpaper: 'wainscot', wall: '#efe3cf', trim: '#b89a5a', floor: '#d9cdb8', floorStyle: 'marble' } },
    lounge: { id: 'sala', kind: 'lounge', name: 'Sala', look: { wallpaper: 'stripes', wall: '#cbdcc4', trim: '#6e8f58', floor: '#7d8fa8', floorStyle: 'carpet' } },
    cafe: { id: 'cafeteria', kind: 'cafe', name: 'Cafetería', look: { wallpaper: 'tile', wall: '#f6e2b8', trim: '#c0703a', floor: '#e8dcc0', floorStyle: 'checker' } },
    bath: { id: 'banos', kind: 'bath', name: 'Baños', look: { wallpaper: 'bath', wall: '#d5ebe8', trim: '#6aa8a0', floor: '#cfe4e0', floorStyle: 'checker' } },
};

function needsLamp(report) {
    if (!report) return false;
    if (report.approvalStatus === 'overdue') return true;
    return Boolean(report.adjustmentCounts && report.adjustmentCounts.pending > 0);
}

function stationsSource(reports, clients, projects) {
    const visible = (reports || []).filter((report) => report && !report.isDraft && report.clientId && report.projectId);
    const clientIds = new Set(visible.map((report) => report.clientId));
    const projectIds = new Set(visible.map((report) => report.projectId));
    const clientById = new Map();
    const projectById = new Map();
    (clients || []).forEach((client) => {
        if (client && clientIds.has(client.id)) clientById.set(client.id, client);
    });
    (projects || []).forEach((project) => {
        if (project && projectIds.has(project.id)) projectById.set(project.id, project);
    });
    visible.forEach((report) => {
        if (!clientById.has(report.clientId)) {
            clientById.set(report.clientId, { id: report.clientId, nombre: report.cliente || 'Cliente' });
        }
        if (!projectById.has(report.projectId)) {
            projectById.set(report.projectId, {
                id: report.projectId,
                nombre: report.proyecto || 'Proyecto',
                clientId: report.clientId,
            });
        }
    });
    const orderedClients = [...clientById.values()].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'));
    return { visible, orderedClients, projectById };
}

function commonRoom(spec, width) {
    return { id: spec.id, kind: spec.kind, name: spec.name, common: true, look: spec.look, w: width };
}

/**
 * Arma el edificio a partir de los reportes visibles del usuario.
 */
export function buildOffice(reports, clients, projects) {
    const { visible, orderedClients, projectById } = stationsSource(reports, clients, projects);

    const clientFloors = orderedClients.map((client) => {
        const list = [...projectById.values()]
            .filter((project) => project.clientId === client.id)
            .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'))
            .slice(0, 4);
        const style = LOOKS[hashId(client.id) % LOOKS.length];
        const look = { wallpaper: style.wallpaper, wall: style.wall, trim: style.trim, floor: style.floor, floorStyle: 'wood' };
        const rooms = (list.length ? list : [{ id: `${client.id}-vacio`, nombre: 'Sin proyecto', empty: true }]).map((project) => ({
            id: project.id,
            kind: project.empty ? 'empty' : 'office',
            name: project.nombre || 'Proyecto',
            projectId: project.empty ? '' : project.id,
            empty: Boolean(project.empty),
            alert: visible.some((report) => report.projectId === project.id && needsLamp(report)),
            decor: hashId(project.id) % 3,
            look,
            w: ROOM_W,
        }));
        return { id: client.id, kind: 'client', name: client.nombre || 'Cliente', client, look, rooms };
    });

    const cols = Math.max(2, ...clientFloors.map((floor) => floor.rooms.length));
    const innerW = cols * ROOM_W;
    const floors = [];
    if (cols >= 4) {
        floors.push({
            id: 'lobby',
            kind: 'common',
            name: 'Planta baja',
            rooms: ['lobby', 'lounge', 'cafe', 'bath'].map((key) => commonRoom(COMMON[key], innerW / 4)),
        });
    } else {
        floors.push({
            id: 'lobby',
            kind: 'common',
            name: 'Planta baja',
            rooms: ['lobby', 'lounge'].map((key) => commonRoom(COMMON[key], innerW / 2)),
        });
        floors.push({
            id: 'amenidades',
            kind: 'common',
            name: 'Amenidades',
            rooms: ['cafe', 'bath'].map((key) => commonRoom(COMMON[key], innerW / 2)),
        });
    }
    clientFloors.forEach((floor) => {
        const used = floor.rooms.length * ROOM_W;
        if (used < innerW) {
            floor.rooms.push({ id: `${floor.id}-pasillo`, kind: 'hall', name: 'Pasillo', look: floor.look, w: innerW - used });
        }
        floors.push(floor);
    });

    const x0 = 18;
    const innerX = x0 + BRICK;
    floors.forEach((floor, index) => {
        floor.index = index;
        let x = innerX;
        floor.rooms.forEach((room) => {
            room.x = x;
            room.floor = index;
            room.key = `${index}:${room.id}`;
            x += room.w;
            const scene = sceneFor(room);
            room.items = scene.items;
            room.spots = scene.spots;
            room.work = scene.work;
        });
    });

    const elevX = innerX + innerW;
    const width = elevX + ELEV_W + BRICK + 18;
    const height = ROOF + floors.length * FLOOR_H + GROUND;
    const sections = [
        { id: 'all', name: 'Todo el edificio' },
        ...floors.map((floor) => ({ id: floor.id, name: floor.name })),
    ];
    const commonRooms = [];
    floors.forEach((floor) => floor.rooms.forEach((room) => {
        if (room.common) commonRooms.push(room);
    }));

    return {
        floors,
        cols,
        innerW,
        innerX,
        x0,
        elevX,
        width,
        height,
        floorH: FLOOR_H,
        roomW: ROOM_W,
        sections,
        commonRooms,
    };
}

/** Borde superior del piso. El 0 es la planta baja, abajo del todo. */
export function floorTop(map, index) {
    const safe = Math.max(0, Math.min(map.floors.length - 1, index));
    return ROOF + (map.floors.length - 1 - safe) * FLOOR_H;
}

/** Centro de la cabina del elevador. */
export function elevatorStandX(map) {
    return map.elevX + ELEV_W / 2;
}

/** Punto del pasillo donde se espera el elevador. */
export function elevatorWaitX(map) {
    return map.elevX - 30;
}

/** Y del suelo para un piso entero o para el elevador a medio camino. */
export function feetY(map, floorValue) {
    const maxFloor = Math.max(0, map.floors.length - 1);
    const clamped = Math.max(0, Math.min(maxFloor, floorValue));
    const base = Math.floor(clamped + 1e-4);
    const frac = clamped - base;
    const y0 = floorTop(map, base) + FLOOR_H - FEET;
    const y1 = floorTop(map, Math.min(maxFloor, base + 1)) + FLOOR_H - FEET;
    return y0 + (y1 - y0) * frac;
}

/** Sala que contiene la x dada dentro de un piso. */
export function roomAt(map, floorIndex, x) {
    const floor = map.floors[floorIndex];
    if (!floor) return null;
    return floor.rooms.find((room) => x >= room.x && x < room.x + room.w) || null;
}

export function boundsFor(map, section) {
    if (!section || section === 'all') {
        return { minX: 0, minY: 0, maxX: map.width, maxY: map.height, cx: map.width / 2, cy: map.height / 2 };
    }
    const floor = map.floors.find((item) => item.id === section);
    if (!floor) return boundsFor(map, 'all');
    const y = floorTop(map, floor.index);
    return {
        minX: 0,
        minY: Math.max(0, y - 16),
        maxX: map.width,
        maxY: y + FLOOR_H + 16,
        cx: map.width / 2,
        cy: y + FLOOR_H / 2,
    };
}

export function cameraPanToFit(bounds, stageW, stageH, zoom) {
    return {
        panX: stageW / 2 - bounds.cx * zoom,
        panY: stageH / 2 - bounds.cy * zoom,
    };
}
